import React, { useEffect, useState } from 'react';

import { BooleanInput, Button, FormDataConsumer, FormWithRedirect, NumberInput, SaveButton, TextInput, minValue, required, useNotify } from 'react-admin';
import { useTranslate } from '../common/language';
import { apiUrl } from '../common/constants';
import { headersAPIObjectAuth } from '../common/utils';
import { validateInteger } from './Validator';

import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import Collapse from '@material-ui/core/Collapse';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import LinearProgress from '@material-ui/core/LinearProgress';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import MuiTextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import DeleteIcon from '@material-ui/icons/Delete';
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep';
import EditIcon from '@material-ui/icons/Edit';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import RefreshIcon from '@material-ui/icons/Refresh';
import VisibilityIcon from '@material-ui/icons/Visibility';
import IconCancel from '@material-ui/icons/Cancel';

const fetchJson = (url, options = {}) => fetch(url, {
  ...options,
  headers: headersAPIObjectAuth()
}).then(async response => {
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.message || 'Error connectant amb la API...');
  }
  if (response.status === 204) return null;
  return response.json();
});

const formatTtl = (ttl, translate) => {
  if (ttl === null || ttl === undefined) return '';
  if (ttl === 0) return translate('redis_cache.cache_disabled');
  if (ttl === -1) return translate('redis_cache.ttl_forever');
  if (ttl === -2) return translate('redis_cache.ttl_not_found');
  const hours = Math.floor(ttl / 3600);
  const minutes = Math.floor((ttl % 3600) / 60);
  const seconds = ttl % 60;
  if (hours > 0) return hours + ' h ' + minutes + ' min';
  if (minutes > 0) return minutes + ' min ' + seconds + ' s';
  return seconds + ' s';
};

const formatBytes = bytes => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex++;
  }
  return value.toLocaleString('es-ES', {
    minimumFractionDigits: unitIndex === 0 ? 0 : 2,
    maximumFractionDigits: unitIndex === 0 ? 0 : 2
  }) + ' ' + units[unitIndex];
};

const formatContent = content => {
  if (content === null || content === undefined) return '';
  return typeof content === 'string' ? content : JSON.stringify(content, null, 2);
};

const RedisPanel = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const isSuperAdmin = ['SIFAC'].includes(localStorage.getItem('permissions_groups'));
  const [summary, setSummary] = useState();
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState();
  const [keys, setKeys] = useState([]);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState('0');
  const [keysFinished, setKeysFinished] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [purging, setPurging] = useState(false);
  const [editPattern, setEditPattern] = useState();
  const [savingTtl, setSavingTtl] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState();
  const [contentDialogKey, setContentDialogKey] = useState();
  const [contentDialogItem, setContentDialogItem] = useState();
  const pageSize = 100;

  const loadSummary = () => {
    setLoading(true);
    fetchJson(apiUrl + '/redis/summary')
      .then(setSummary)
      .catch(error => notify(error.message, 'error'))
      .finally(() => setLoading(false));
  };

  const loadKeys = (pattern, cursor = '0', append = false, query = filterText) => {
    if (!pattern) return;
    append ? setLoadingMore(true) : setLoadingKeys(true);
    fetchJson(apiUrl + '/redis/keys/page?pattern=' + encodeURIComponent(pattern)
      + '&q=' + encodeURIComponent(query) + '&cursor=' + encodeURIComponent(cursor) + '&count=' + pageSize)
      .then(data => {
        const page = data && data.keys ? data.keys : [];
        setKeys(current => append ? [...current, ...page] : page);
        setNextCursor(data && data.nextCursor ? data.nextCursor : '0');
        setKeysFinished(data ? data.finished : true);
        if (!append) setSelectedKeys([]);
      })
      .catch(error => notify(error.message, 'error'))
      .finally(() => { setLoadingKeys(false); setLoadingMore(false); });
  };

  useEffect(() => { if (isSuperAdmin) loadSummary(); }, [isSuperAdmin]);

  useEffect(() => {
    if (!expanded) return;
    const timeout = setTimeout(() => loadKeys(expanded.pattern, '0', false, filterText), 350);
    return () => clearTimeout(timeout);
  }, [filterText]);

  const toggleExpanded = pattern => {
    if (expanded && expanded.id === pattern.id) {
      setExpanded();
      setKeys([]);
      return;
    }
    setExpanded(pattern);
    setFilterText('');
    setSelectedKeys([]);
    loadKeys(pattern.pattern, '0', false, '');
  };

  const refresh = () => {
    loadSummary();
    if (expanded) loadKeys(expanded.pattern, '0', false);
  };

  const purgePattern = pattern => {
    setPurging(true);
    fetchJson(apiUrl + '/redis/pattern?pattern=' + encodeURIComponent(pattern.pattern), { method: 'DELETE' })
      .then(data => notify(translate('redis_cache.purged') + ': ' + (data && data.deleted ? data.deleted : 0), 'success'))
      .then(refresh)
      .catch(error => notify(error.message, 'error'))
      .finally(() => setPurging(false));
  };

  const purgeKey = key => {
    setPurging(true);
    fetchJson(apiUrl + '/redis/key?key=' + encodeURIComponent(key), { method: 'DELETE' })
      .then(() => notify(translate('redis_cache.key_purged'), 'success'))
      .then(refresh)
      .catch(error => notify(error.message, 'error'))
      .finally(() => setPurging(false));
  };

  const purgeSelected = () => {
    setPurging(true);
    Promise.all(selectedKeys.map(key => fetchJson(apiUrl + '/redis/key?key=' + encodeURIComponent(key), { method: 'DELETE' })))
      .then(() => notify(translate('redis_cache.purged') + ': ' + selectedKeys.length, 'success'))
      .then(() => { setSelectedKeys([]); refresh(); })
      .catch(error => notify(error.message, 'error'))
      .finally(() => setPurging(false));
  };

  const openEdit = pattern => {
    setEditPattern(pattern);
  };

  const saveConfiguration = values => {
    setSavingTtl(true);
    return fetchJson(apiUrl + '/redis/patterns/' + editPattern.id + '/ttl', {
      method: 'PATCH',
      body: JSON.stringify({
        ttl_seconds: Number(values.ttl_seconds),
        priority: Number(values.priority)
      })
    })
      .then(() => {
        notify(translate('redis_cache.configuration_updated'), 'success');
        setEditPattern();
        loadSummary();
      })
      .catch(error => notify(error.message, 'error'))
      .finally(() => setSavingTtl(false));
  };

  const openConfirm = (message, action) => setConfirmDialog({ message, action });
  const confirm = () => {
    if (confirmDialog && confirmDialog.action) confirmDialog.action();
    setConfirmDialog();
  };

  const toggleKey = key => setSelectedKeys(selectedKeys.includes(key)
    ? selectedKeys.filter(item => item !== key)
    : [...selectedKeys, key]);
  const allSelected = keys.length > 0 && keys.every(item => selectedKeys.includes(item.key));
  const someSelected = keys.some(item => selectedKeys.includes(item.key));
  const toggleAll = () => setSelectedKeys(allSelected ? [] : keys.map(item => item.key));

  const openContent = key => {
    setContentDialogKey(key);
    setContentDialogItem();
    fetchJson(apiUrl + '/redis/key/details?key=' + encodeURIComponent(key))
      .then(setContentDialogItem)
      .catch(error => notify(error.message, 'error'));
  };

  if (!isSuperAdmin) return null;
  const patterns = summary && summary.patterns ? summary.patterns : [];

  return <>
    <Box p={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body1">
          {translate('redis_cache.total_cache')}: {summary ? summary.keys : 0} {translate('redis_cache.keys')}, {formatBytes(summary ? summary.sizeBytes : 0)}
        </Typography>
        <Button label={translate('redis_cache.refresh')} onClick={refresh} disabled={loading || purging}><RefreshIcon /></Button>
      </Box>
      {loading ? <LinearProgress /> : null}
      <Table size="small">
        <TableHead><TableRow>
          <TableCell />
          <TableCell>{translate('redis_cache.internal_id')}</TableCell>
          <TableCell>{translate('redis_cache.pattern')}</TableCell>
          <TableCell>{translate('redis_cache.description')}</TableCell>
          <TableCell align="right">{translate('redis_cache.key_count')}</TableCell>
          <TableCell align="right">{translate('redis_cache.total_size')}</TableCell>
          <TableCell align="right">{translate('redis_cache.ttl_seconds')}</TableCell>
          <TableCell align="right">{translate('common.actions')}</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {patterns.map(pattern => {
            const isExpanded = expanded && expanded.id === pattern.id;
            return <React.Fragment key={pattern.id}>
              <TableRow hover>
                <TableCell><IconButton size="small" onClick={() => toggleExpanded(pattern)}>{isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}</IconButton></TableCell>
                <TableCell><strong>{pattern.internal_id}</strong></TableCell>
                <TableCell style={{ whiteSpace: 'nowrap' }}>{pattern.pattern}</TableCell>
                <TableCell>{pattern.description}</TableCell>
                <TableCell align="right">{pattern.keys || 0}</TableCell>
                <TableCell align="right">{formatBytes(pattern.sizeBytes)}</TableCell>
                <TableCell align="right">{formatTtl(pattern.ttl_seconds, translate)} ({pattern.ttl_seconds} s)</TableCell>
                <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                  <IconButton title={translate('redis_cache.modify_configuration')} onClick={() => openEdit(pattern)} disabled={purging}><EditIcon /></IconButton>
                  <IconButton title={translate('redis_cache.purge_all')} onClick={() => openConfirm(translate('redis_cache.purge_all_confirm'), () => purgePattern(pattern))} disabled={purging}><DeleteSweepIcon /></IconButton>
                </TableCell>
              </TableRow>
              <TableRow><TableCell colSpan={8} style={{ paddingBottom: 0, paddingTop: 0 }}>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box margin={2}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Box flexGrow={1} mr={2}><MuiTextField fullWidth size="small" variant="outlined" label={translate('redis_cache.filter')} value={filterText} onChange={event => setFilterText(event.target.value)} /></Box>
                      <Button label={translate('redis_cache.purge_selected') + ' (' + selectedKeys.length + ')'} onClick={() => openConfirm(translate('redis_cache.purge_selected_confirm'), purgeSelected)} disabled={!selectedKeys.length || purging}><DeleteIcon /></Button>
                    </Box>
                    {loadingKeys ? <LinearProgress /> : null}
                    <Table size="small">
                      <TableHead><TableRow>
                        <TableCell padding="checkbox"><Checkbox checked={allSelected} indeterminate={!allSelected && someSelected} onChange={toggleAll} disabled={!keys.length || purging} /></TableCell>
                        <TableCell>{translate('redis_cache.key')}</TableCell>
                        <TableCell>{translate('redis_cache.ttl')}</TableCell>
                        <TableCell>{translate('redis_cache.size')}</TableCell>
                        <TableCell>{translate('redis_cache.type')}</TableCell>
                        <TableCell align="right">{translate('common.actions')}</TableCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {keys.map(item => <TableRow key={item.key}>
                          <TableCell padding="checkbox"><Checkbox checked={selectedKeys.includes(item.key)} onChange={() => toggleKey(item.key)} disabled={purging} /></TableCell>
                          <TableCell style={{ wordBreak: 'break-all' }}>{item.key}</TableCell>
                          <TableCell>{formatTtl(item.ttl, translate)}</TableCell>
                          <TableCell>{formatBytes(item.sizeBytes)}</TableCell>
                          <TableCell>{item.type}</TableCell>
                          <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                            <IconButton onClick={() => openContent(item.key)}><VisibilityIcon /></IconButton>
                            <IconButton onClick={() => openConfirm(translate('redis_cache.purge_key_confirm'), () => purgeKey(item.key))} disabled={purging}><DeleteIcon /></IconButton>
                          </TableCell>
                        </TableRow>)}
                        {!loadingKeys && !keys.length ? <TableRow><TableCell colSpan={6} align="center">{translate('redis_cache.no_keys')}</TableCell></TableRow> : null}
                      </TableBody>
                    </Table>
                    <Box mt={1} textAlign="center"><Button label={translate('redis_cache.load_more')} onClick={() => loadKeys(pattern.pattern, nextCursor, true)} disabled={keysFinished || loadingMore || loadingKeys} /></Box>
                    {loadingMore ? <LinearProgress /> : null}
                  </Box>
                </Collapse>
              </TableCell></TableRow>
            </React.Fragment>;
          })}
        </TableBody>
      </Table>
    </Box>

    <Dialog open={!!editPattern} onClose={() => !savingTtl && setEditPattern()} fullWidth maxWidth="xs">
      <DialogTitle>{translate('redis_cache.modify_configuration')}</DialogTitle>
      <FormWithRedirect
        key={editPattern ? editPattern.id : 'redis-pattern-closed'}
        resource="redis_patterns"
        save={saveConfiguration}
        initialValues={editPattern}
        render={({ handleSubmitWithRedirect, pristine, saving }) => <>
          <DialogContent>
            <Typography variant="body2" gutterBottom>{editPattern ? editPattern.internal_id : ''}</Typography>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <NumberInput fullWidth source="ttl_seconds" step={1} label={translate('redis_cache.ttl_seconds')} validate={[required(), minValue(0), validateInteger(translate('common.validation.number'))]} />
                <Typography variant="caption">{translate('redis_cache.ttl_zero_help')}</Typography>
              </Grid>
              <Grid item xs={12}>
                <NumberInput fullWidth source="priority" step={1} label={translate('redis_cache.priority')} validate={[required(), minValue(0), validateInteger(translate('common.validation.number'))]} />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button label="ra.action.cancel" onClick={() => setEditPattern()} disabled={savingTtl}>
              <IconCancel />
            </Button>
            <SaveButton
              handleSubmitWithRedirect={handleSubmitWithRedirect}
              pristine={pristine}
              saving={saving}
              disabled={savingTtl}
            />
          </DialogActions>
        </>}
      />
    </Dialog>

    <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog()} fullWidth maxWidth="xs">
      <DialogTitle>{translate('ra.message.delete_title')}</DialogTitle>
      <DialogContent><Typography>{confirmDialog ? confirmDialog.message : ''}</Typography></DialogContent>
      <DialogActions><Button label="ra.action.cancel" onClick={() => setConfirmDialog()} /><Button label={translate('redis_cache.purge')} onClick={confirm} disabled={purging}><DeleteIcon /></Button></DialogActions>
    </Dialog>

    <Dialog open={!!contentDialogKey} onClose={() => setContentDialogKey()} fullWidth maxWidth="md">
      <DialogTitle>{contentDialogKey || ''}</DialogTitle>
      <DialogContent>{!contentDialogItem ? <LinearProgress /> : <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 500, overflow: 'auto' }}>{formatContent(contentDialogItem.content)}</pre>}</DialogContent>
      <DialogActions><Button label={translate('redis_cache.copy_content')} onClick={() => navigator.clipboard.writeText(formatContent(contentDialogItem ? contentDialogItem.content : ''))} disabled={!contentDialogItem}><FileCopyIcon /></Button><Button label="ra.action.close" onClick={() => setContentDialogKey()} /></DialogActions>
    </Dialog>
  </>;
};

const CdnPanel = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [settings, setSettings] = useState();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchJson(apiUrl + '/utils/general-settings/0')
      .then(setSettings)
      .catch(error => notify(error.message, 'error'))
      .finally(() => setLoading(false));
  }, []);

  const save = values => {
    setSaving(true);
    return fetchJson(apiUrl + '/utils/general-settings/0', {
      method: 'PUT',
      body: JSON.stringify({
        public_docs_cdn_enabled: !!values.public_docs_cdn_enabled,
        public_docs_cdn_url: values.public_docs_cdn_url || ''
      })
    })
      .then(data => {
        setSettings(data);
        notify(translate('redis_cache.cdn_updated'), 'success');
      })
      .catch(error => notify(error.message, 'error'))
      .finally(() => setSaving(false));
  };

  if (loading) return <Box p={2}><LinearProgress /></Box>;
  if (!settings) return null;

  return <Box p={2}>
    <FormWithRedirect
      resource="utils/general-settings"
      initialValues={settings}
      save={save}
      render={({ handleSubmitWithRedirect, pristine }) => <>
        <BooleanInput
          source="public_docs_cdn_enabled"
          label={translate('redis_cache.cdn_enabled')}
        />
        <FormDataConsumer>
          {({ formData }) => <TextInput
            fullWidth
            source="public_docs_cdn_url"
            label={translate('redis_cache.cdn_url')}
            disabled={!formData.public_docs_cdn_enabled}
          />}
        </FormDataConsumer>
        <Box mt={2}>
          <SaveButton
            handleSubmitWithRedirect={handleSubmitWithRedirect}
            pristine={pristine}
            saving={saving}
            disabled={saving}
          />
        </Box>
      </>}
    />
  </Box>;
};

export const RedisCache = () => {
  const translate = useTranslate();
  const isSuperAdmin = ['SIFAC'].includes(localStorage.getItem('permissions_groups'));
  const [activeTab, setActiveTab] = useState(0);

  if (!isSuperAdmin) return null;

  return <>
    <br />
    <Typography variant="h5" className="grey8" align="center">{translate('settings.cache_management')}</Typography>
    <Box className="settings_box" mt={2}>
      <Tabs value={activeTab} onChange={(event, value) => setActiveTab(value)} indicatorColor="primary" textColor="primary">
        <Tab label="Redis" />
        <Tab label="CDN" />
      </Tabs>
      {activeTab === 0 ? <RedisPanel /> : <CdnPanel />}
    </Box>
  </>;
};
